/** Needs (rest/food/joy) drain & restore, and mood derivation. */
import type { City, Building, Colonist } from './types';
import { NEED_KEYS } from './types';
import { SIM } from './config';
import { drainMul, traitMoodFlat } from './colonist';

/** Advance a colonist's needs for `dt`. The current activity restores one need;
 *  the rest drain. Eating pulls a meal from the building's larder. */
export function updateNeeds(b: Building, c: Colonist, dt: number): void {
  const restoring =
    c.activity === 'sleep'
      ? 'rest'
      : c.activity === 'eat'
        ? 'food'
        : c.activity === 'relax'
          ? 'joy'
          : c.activity === 'recover'
            ? 'rest'
            : null;

  for (const need of NEED_KEYS) {
    if (need === restoring) {
      if (need === 'food') {
        if (b.res.meals > 0) {
          b.res.meals = Math.max(0, b.res.meals - dt * 0.6);
          c.needs.food = Math.min(100, c.needs.food + SIM.RESTORE.food * dt);
        }
      } else {
        const rate = c.activity === 'recover' ? SIM.RESTORE.rest * 0.6 : SIM.RESTORE[need];
        c.needs[need] = Math.min(100, c.needs[need] + rate * dt);
      }
    } else {
      const slow = c.activity === 'sleep' || c.activity === 'recover' ? 0.45 : 1;
      c.needs[need] = Math.max(0, c.needs[need] - SIM.DRAIN[need] * drainMul(c, need) * slow * dt);
    }
  }
}

/** Recompute cached mood from needs, traits, and the city-wide event climate. */
export function updateMood(city: City, c: Colonist): void {
  let mood = SIM.MOOD_BASE + traitMoodFlat(c);
  for (const need of NEED_KEYS) {
    mood += (c.needs[need] - 50) * 0.18;
    if (c.needs[need] < SIM.CRITICAL) mood -= 16;
  }
  for (const e of city.activeEvents) if (e.id === 'morale-slump') mood -= 14;
  if (c.downUntil > city.t) mood -= 10;
  c.mood = Math.max(0, Math.min(100, mood));
}

/** The most urgent need below its seek threshold, or null. */
export function urgentNeed(c: Colonist): 'rest' | 'food' | 'joy' | null {
  let worst: 'rest' | 'food' | 'joy' | null = null;
  let worstRatio = Infinity;
  for (const need of NEED_KEYS) {
    if (c.needs[need] < SIM.SEEK_THRESHOLD[need]) {
      const ratio = c.needs[need] / SIM.SEEK_THRESHOLD[need];
      if (ratio < worstRatio) {
        worstRatio = ratio;
        worst = need;
      }
    }
  }
  return worst;
}
